import * as React from 'react';
import { FieldWrapper } from '@progress/kendo-react-form';
import { FloatingLabel, Error, Hint } from '@progress/kendo-react-labels';
import AsyncMultiSelect from '../inputs/AsyncMultiSelect';
// eslint-disable-next-line no-unused-vars
import BaseFormControl from './BaseFormControl';

/**
 * An async multi select form field.
 *
 * @typedef {React.ComponentProps<BaseFormControl>} BaseFieldRenderProps
 * @typedef {React.ComponentPropsWithoutRef<AsyncMultiSelect>} AsyncMultiSelectProps
 *
 * @param {BaseFieldRenderProps & AsyncMultiSelectProps} fieldRenderProps
 * @returns {React.ReactElement} Async multi select form field.
 */
const FormAsyncMultiSelect = (fieldRenderProps) => {
  const {
    validationMessage,
    touched,
    label,
    id,
    name,
    valid,
    disabled,
    hint,
    wrapperStyle,
    wrapperClassName,
    optional,
    value,
    ...others
  } = fieldRenderProps;
  const editorId = id ?? name;
  const editorRef = React.useRef(null);
  const showValidationMessage = touched && validationMessage;
  const showHint = !showValidationMessage && hint;
  const hintId = showHint ? `${editorId}_hint` : '';
  const errorId = showValidationMessage ? `${editorId}_error` : '';
  const labelId = label ? `${editorId}_label` : '';
  return (
    <FieldWrapper style={wrapperStyle} className={wrapperClassName}>
      <FloatingLabel
        label={label}
        id={labelId}
        optional={optional}
        editorRef={editorRef}
        editorId={editorId}
        editorValid={valid}
        editorDisabled={disabled}
        editorValue={!value || value.length === 0 ? null : value}
      >
        <AsyncMultiSelect
          ariaLabelledBy={labelId}
          ariaDescribedBy={`${hintId} ${errorId}`}
          ref={editorRef}
          valid={valid}
          id={editorId}
          disabled={disabled}
          optional={optional}
          value={value}
          dataTestId={editorId}
          {...others}
        />
      </FloatingLabel>
      {showHint && <Hint id={hintId}>{hint}</Hint>}
      {showValidationMessage && <Error id={errorId}>{validationMessage}</Error>}
    </FieldWrapper>
  );
};

export default FormAsyncMultiSelect;
